import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { t } from "../i18n";

interface Props {
  /** Called when the customer has picked a photo AND typed a name. The host
   *  (App.tsx) takes it from here — uploads the file, calls /generate, and
   *  swaps to the loading/preview step. */
  onSubmit: (file: File, fileUrl: string, name: string) => void;
  /** Fires whenever the form's readiness flips (has file AND non-empty name).
   *  App uses this to enable/disable the PDP's external Preview button. */
  onReadyChange?: (ready: boolean) => void;
  /** Widget mode hides the in-card Generate button — the PDP's Preview
   *  button drives submission via the InujoyWidget API. */
  hideSubmitButton?: boolean;
}

export interface SingleFormStepHandle {
  /** Validates internal state and calls onSubmit if ready. Returns true if
   *  submission fired, false otherwise (caller may surface the error). */
  submit: () => boolean;
}

const MAX_BYTES = 12 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic"];
const MAX_OUTPUT_DIM = 2048;
const CROP_ASPECT = 1;

type View = "empty" | "cropping" | "ready";

export const SingleFormStep = forwardRef<SingleFormStepHandle, Props>(function SingleFormStep(
  { onSubmit, onReadyChange, hideSubmitButton },
  ref,
) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<View>("empty");

  // Original (pre-crop) — kept so "Use original" can fall back without re-pick.
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string>("");

  // Final (what we'll submit) — either the cropped File or the original.
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>("");

  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);

  // react-easy-crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropping, setCropping] = useState(false);

  // Name is optional — only the cropped/original photo is required. The
  // signature panel in PreviewStep lets the customer add a name later if
  // they want it printed on the canvas.
  const ready = view === "ready" && file !== null;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  // Free any in-flight blob URLs on unmount.
  useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
      if (fileUrl && fileUrl !== originalUrl) URL.revokeObjectURL(fileUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build marker — helps verify the new bundle is loaded in the user's browser.
  useEffect(() => {
    console.log("[SingleFormStep] build v2026-05-18 · crop + change-photo enabled");
  }, []);

  function pickFile(f: File) {
    setError(null);
    if (f.size > MAX_BYTES) {
      setError(t("upload.error.too_big"));
      return;
    }
    if (!ALLOWED.includes(f.type) && f.type !== "") {
      setError(t("upload.error.bad_type"));
      return;
    }

    // Revoke prior URLs to free memory.
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (fileUrl && fileUrl !== originalUrl) URL.revokeObjectURL(fileUrl);

    const url = URL.createObjectURL(f);
    setOriginalFile(f);
    setOriginalUrl(url);
    setFile(null);
    setFileUrl("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setView("cropping");
  }

  function onPickInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
    // Reset so re-selecting the same file path still fires onChange.
    e.target.value = "";
  }

  function openPicker() {
    fileRef.current?.click();
  }

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function useCropped() {
    if (!croppedAreaPixels || !originalUrl) return;
    setCropping(true);
    setError(null);
    try {
      const out = await produceCroppedFile(originalUrl, croppedAreaPixels);
      const url = URL.createObjectURL(out);
      setFile(out);
      setFileUrl(url);
      setView("ready");
    } catch {
      setError(t("upload.error.crop_failed"));
    } finally {
      setCropping(false);
    }
  }

  function useOriginal() {
    if (!originalFile) return;
    setFile(originalFile);
    setFileUrl(originalUrl);
    setView("ready");
  }

  function handleSubmit(): boolean {
    if (view !== "ready" || !file) {
      setError(view === "cropping" ? t("upload.error.confirm_crop") : t("upload.error.no_photo"));
      return false;
    }
    onSubmit(file, fileUrl, name.trim());
    return true;
  }

  useImperativeHandle(ref, () => ({ submit: handleSubmit }), [view, file, fileUrl, name]);

  return (
    <div className="single-form">
      {/* ── Upload card ── */}
      <div
        className={
          "single-form__upload" +
          (drag ? " is-drag" : "") +
          (view !== "empty" ? " has-file" : "") +
          (view === "cropping" ? " is-cropping" : "")
        }
        onClick={view === "empty" ? openPicker : undefined}
        onDragOver={(e) => {
          if (view !== "empty") return;
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          if (view !== "empty") return;
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files[0];
          if (f) pickFile(f);
        }}
      >
        {view === "empty" && (
          <div className="single-form__dropzone">
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <strong>{t("upload.tap")}</strong>
            <small>{t("upload.hint")}</small>
          </div>
        )}

        {view === "cropping" && originalUrl && (
          <div className="single-form__crop" onClick={(e) => e.stopPropagation()}>
            <div className="single-form__crop-hint">
              {t("upload.crop.hint")}
            </div>
            <div className="single-form__crop-stage">
              <Cropper
                image={originalUrl}
                crop={crop}
                zoom={zoom}
                aspect={CROP_ASPECT}
                minZoom={1}
                maxZoom={3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid
                objectFit="contain"
              />
            </div>
            <div className="single-form__crop-controls">
              <span aria-hidden>−</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                aria-label={t("upload.zoom_label")}
              />
              <span aria-hidden>+</span>
            </div>
            <div className="single-form__crop-actions">
              <button type="button" className="single-form__crop-skip" onClick={useOriginal} disabled={cropping}>
                {t("upload.crop.use_original")}
              </button>
              <button type="button" className="single-form__crop-ok" onClick={() => void useCropped()} disabled={cropping || !croppedAreaPixels}>
                {cropping ? t("upload.crop.cropping") : t("upload.crop.use_cropped")}
              </button>
            </div>
          </div>
        )}

        {view === "ready" && fileUrl && (
          <div className="single-form__ready" onClick={(e) => e.stopPropagation()}>
            <img className="single-form__thumb" src={fileUrl} alt={t("upload.thumb_alt")} />
            <div className="single-form__ready-meta">
              <div className="single-form__ready-check">
                <span aria-hidden>✓</span> {t("upload.photo_ready")}
              </div>
              <button type="button" className="single-form__change-link" onClick={openPicker}>
                <span aria-hidden>🔄</span> {t("upload.change_photo")}
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onPickInput}
        />
      </div>

      {/* Always-visible "pick a different photo" link — rendered OUTSIDE the
          conditional state blocks so it works regardless of whether the user
          is mid-crop, in ready state, or saw a render edge case. */}
      {(view === "cropping" || view === "ready") && (
        <button
          type="button"
          className="single-form__pick-another"
          onClick={openPicker}
        >
          <span aria-hidden>📷</span> {t("upload.pick_another")}
        </button>
      )}

      {/* Name input removed — was redundant with the Signature panel on the
          Preview screen which already lets the customer type + style a name.
          Keeping a state value of "" so handleSubmit / parent code doesn't
          break. Name can be added in the signature step after preview. */}

      {error && (
        <div className="single-form__error" role="alert">
          {error}
        </div>
      )}

      {!hideSubmitButton && (
        <button
          type="button"
          className="btn btn-primary single-form__submit"
          onClick={handleSubmit}
          disabled={!ready}
        >
          {t("upload.submit")}
        </button>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────
// Crop helpers — produce a JPEG File from the area the user selected.
// Capped to MAX_OUTPUT_DIM so a 4000px phone shot doesn't bloat upload.
// ─────────────────────────────────────────────────────────────────────
async function produceCroppedFile(imageUrl: string, area: Area): Promise<File> {
  const img = await loadImage(imageUrl);
  const scale = Math.min(MAX_OUTPUT_DIM / area.width, 1);
  const outW = Math.round(area.width * scale);
  const outH = Math.round(area.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unsupported");

  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92),
  );
  if (!blob) throw new Error("blob_creation_failed");

  return new File([blob], "cropped-dad.jpg", { type: "image/jpeg" });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!url.startsWith("blob:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = url;
  });
}
